import { SelectionCalculator } from '@sravni/creditselection-react/lib/SelectionCalculator';
import getConfig from 'next/config';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';

import { AMOUNTS } from 'constants/amounts';
import type { TPeriod } from 'constants/dictionaries';
import { PERIODS } from 'constants/periods';

import { isProduction, useAppSelector } from 'shared/lib';
import {
  sendEventOnClickFillManuallyButton,
  sendEventOnClickGosuslugiButton,
  sendEventOnContinueButton,
  sendEventOnGosuslugiSuccessLogin,
} from 'shared/lib/sendGAEvents';
import { useSyncFormFieldsToQueryParams } from 'shared/lib/useSyncFormFieldsToQueryParams';

import { selectCreditPurposeInfoData, useDictionaries } from 'entities/creditForm';
import { isLoggedSelector, useGetIfUserHasEsiaQuery } from 'entities/user';
import { selectMinRateFromVitrinsOffers } from 'entities/vitrins/model/vitrins.selectors';

import { useOnCalculatorFieldChange } from 'widgets/LoansHeroFormSection/lib/useOnCalculatorFieldChange';
import { HeroCalculatorTexts } from 'widgets/LoansHeroFormSection/ui/HeroCalculator/HeroCalculator.texts';

type Props = {
  className?: string;
  onRedirectToForm?: () => void;
};

export const HeroCalculator: FC<Props> = (props) => {
  const { className, onRedirectToForm } = props;
  const onFieldChange = useOnCalculatorFieldChange();
  const isLoggedIn = useAppSelector(isLoggedSelector);
  const minRate = useAppSelector(selectMinRateFromVitrinsOffers);
  const { data: isUserHasEsia } = useGetIfUserHasEsiaQuery();
  const { publicRuntimeConfig } = getConfig();
  const { purposes: purposeOptions, periods: periodOptions } = useDictionaries(['purposes', 'periods']);
  const creditPurposeFormData = useAppSelector(selectCreditPurposeInfoData);

  useSyncFormFieldsToQueryParams({
    amount: creditPurposeFormData?.amount,
    period: creditPurposeFormData?.period?.value,
    purpose: creditPurposeFormData?.purpose?.value,
  });

  const onCalculatorEvent: ComponentProps<typeof SelectionCalculator>['onEvent'] = useCallback(
    (event) => {
      switch (event.type) {
        case 'CALCULATOR_MOUNT': {
          const { amount, period, purpose } = event.payload || {};
          Object.entries({ amount, period, purpose }).forEach(([key, value]) => {
            if (!value) {
              return;
            }
            switch (key) {
              case 'amount': {
                onFieldChange(key, value.toString());
                break;
              }
              case 'purpose':
              case 'period': {
                onFieldChange(key, value.value);
                break;
              }
              default:
                break;
            }
          });
          break;
        }
        case 'COMPACT_CALCULATOR_MANUAL_BUTTON_CLICK': {
          sendEventOnClickFillManuallyButton();
          onRedirectToForm?.();
          break;
        }
        case 'CALCULATOR_REDIRECT_TO_FORM': {
          sendEventOnGosuslugiSuccessLogin();
          break;
        }
        case 'CALCULATOR_BUTTON_CLICK': {
          sendEventOnClickGosuslugiButton();
          break;
        }
        case 'COMPACT_CALCULATOR_CONTINUE_BUTTON_CLICK': {
          sendEventOnContinueButton();
          onRedirectToForm?.();
          break;
        }
        default:
          break;
      }
    },
    [onRedirectToForm, onFieldChange],
  );

  return (
    <SelectionCalculator
      onFieldChange={onFieldChange}
      onEvent={onCalculatorEvent}
      className={className}
      shouldShowParams={!!minRate}
      externalRate={minRate || undefined}
      variant="compact"
      isLoggedIn={isLoggedIn}
      isLoggedInEsia={!!isUserHasEsia}
      baseUrl={publicRuntimeConfig.headerDomains.base}
      enableAnalytics={isProduction() ? true : 'logs'}
      eventLabelPage={HeroCalculatorTexts.eventLabelPage}
      defaultFormValues={{
        amount: Number(creditPurposeFormData?.amount) || AMOUNTS.defaultFormValue,
        period: creditPurposeFormData?.period?.value || (PERIODS.defaultFormValue as TPeriod),
        purpose: creditPurposeFormData?.purpose?.value,
      }}
      formParams={{
        amount: {
          min: AMOUNTS.amountMin,
          max: AMOUNTS.amountMax,
          step: 1_000,
        },
        periodOptions,
        purposeOptions,
      }}
    />
  );
};
